import { useRouterState } from "@tanstack/react-router";

export type Locale = "en" | "vi" | "el";
export const LOCALES: Locale[] = ["en", "vi", "el"];
export const DEFAULT_LOCALE: Locale = "en";
export const SITE_URL = "https://banana.cards";

/** Pull the locale prefix off a pathname. Returns { locale, rest } where rest starts with "/". */
export function parsePath(pathname: string): { locale: Locale; rest: string } {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length > 0 && (LOCALES as string[]).includes(parts[0])) {
    const locale = parts[0] as Locale;
    if (locale === DEFAULT_LOCALE) return { locale, rest: "/" + parts.slice(1).join("/") };
    return { locale, rest: "/" + parts.slice(1).join("/") };
  }
  return { locale: DEFAULT_LOCALE, rest: pathname || "/" };
}

/** Build a path for a given locale. EN stays at root (`/about`), others get `/vi/about`, `/el/about`. */
export function localizedPath(rest: string, locale: Locale): string {
  const clean = rest.startsWith("/") ? rest : "/" + rest;
  if (locale === DEFAULT_LOCALE) return clean === "" ? "/" : clean;
  if (clean === "/") return "/" + locale;
  return "/" + locale + clean;
}

/** Swap the locale of the current pathname. e.g. /pricing → /vi/pricing, /vi/pricing → /pricing. */
export function swapLocalePath(pathname: string, target: Locale): string {
  const { rest } = parsePath(pathname);
  return localizedPath(rest, target);
}

/** Hook: returns the current locale based on the URL. */
export function useLocale(): Locale {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return parsePath(pathname).locale;
}

/** Hook: current pathname (for switcher targets). */
export function usePathname(): string {
  return useRouterState({ select: (s) => s.location.pathname });
}

/** Build hreflang link entries + canonical for a page. `rest` is the locale-stripped path (e.g. "/pricing"). */
export function alternateLinks(rest: string, currentLocale: Locale) {
  const clean = rest.startsWith("/") ? rest : "/" + rest;
  return {
    canonical: { rel: "canonical", href: SITE_URL + localizedPath(clean, currentLocale) },
    alternates: [
      { rel: "alternate", hrefLang: "en", href: SITE_URL + localizedPath(clean, "en") },
      { rel: "alternate", hrefLang: "vi", href: SITE_URL + localizedPath(clean, "vi") },
      { rel: "alternate", hrefLang: "el", href: SITE_URL + localizedPath(clean, "el") },
      { rel: "alternate", hrefLang: "x-default", href: SITE_URL + localizedPath(clean, "en") },
    ],
  };
}

/** Flat array of head <link> entries (canonical + hreflang alternates) for TanStack head(). */
export function alternateLinkList(rest: string, currentLocale: Locale) {
  const { canonical, alternates } = alternateLinks(rest, currentLocale);
  return [canonical, ...alternates];
}

/** Locale display metadata for the switcher. */
export const LOCALE_META: Record<Locale, { label: string; native: string; flag: string }> = {
  en: { label: "English", native: "English", flag: "🇺🇸" },
  vi: { label: "Vietnamese", native: "Tiếng Việt", flag: "🇻🇳" },
  el: { label: "Greek", native: "Ελληνικά", flag: "🇬🇷" },
};